import {t} from '../localization/translate.js';

const inlineDisplayPillClassNames = [
    'tagPicker__tag',
    'inline-flex',
    'h-18',
    'md:h-20',
    'lg:h-22',
    'xl:h-24',
    'items-center',
    'gap-4',
    'rounded-2xl',
    'fg-bg-neutral-moderate',
    'px-8',
    'whitespace-nowrap',
    'fg-body-xs',
    'fg-text-neutral-default',
];

const inlineDisplayPillRemoveButtonClassNames = [
    'tagPicker__deleteButton',
    'inline-flex',
    'items-center',
    'justify-center',
    'cursor-pointer',
    'leading-none',
    'fg-text-neutral-default',
    'focus:outline-none',
];
const openDropdownLayoutGap = 16;

/**
 * @param {string} value
 * @returns {string}
 */
export function normalizeValue(value) {
    return value
        .trim()
        .toLowerCase()
        .replaceAll(/[_-]+/g, ' ')
        .replaceAll(/\s+/g, ' ');
}

/**
 * @param {string} rawValue
 * @returns {string[]}
 */
export function parseStoredValues(rawValue) {
    return rawValue
        .split(',')
        .map((value) => value.trim())
        .filter((value) => value !== '');
}

/**
 * @param {string} rawValue
 * @returns {string[]}
 */
export function splitFreeformInput(rawValue) {
    return rawValue
        .split(/[\n,]+/g)
        .map((value) => value.trim())
        .filter((value) => value !== '');
}

/**
 * @param {string[]} values
 * @returns {string[]}
 */
export function uniqueValues(values) {
    return [...new Set(values)];
}

/**
 * @param {string} value
 * @returns {string}
 */
export function escapeSelectorValue(value) {
    if (typeof CSS !== 'undefined' && typeof CSS.escape === 'function') {
        return CSS.escape(value);
    }

    return value.replaceAll(/["\\]/g, '\\$&');
}

/**
 * @param {HTMLElement | null | undefined} element
 * @param {boolean} shouldHide
 */
export function setElementHidden(element, shouldHide) {
    if (!(element instanceof HTMLElement)) {
        return;
    }

    element.hidden = shouldHide;
}

export class MultiSelectGroup {
    /**
     * @type {HTMLElement}
     */
    element;

    /**
     * @type {HTMLInputElement|null}
     */
    hiddenInput = null;

    /**
     * @type {HTMLElement|null}
     */
    dropdownTrigger = null;

    /**
     * @type {HTMLButtonElement|null}
     */
    clearTrigger = null;

    /**
     * @type {HTMLElement|null}
     */
    controlShell = null;

    /**
     * @type {HTMLElement|null}
     */
    dropdownPanel = null;

    /**
     * @type {HTMLElement|null}
     */
    displayValueWrapper = null;

    /**
     * @type {HTMLElement|null}
     */
    displayValue = null;

    /**
     * @type {HTMLElement|null}
     */
    displayPlaceholder = null;

    /**
     * @type {HTMLElement|null}
     */
    displayPills = null;

    /**
     * @type {HTMLElement|null}
     */
    controlLabel = null;

    /**
     * @type {HTMLInputElement|null}
     */
    searchInput = null;

    /**
     * @type {HTMLButtonElement|null}
     */
    clearSearchTrigger = null;

    /**
     * @type {HTMLButtonElement|null}
     */
    selectAllTrigger = null;

    /**
     * @type {HTMLElement|null}
     */
    optionList = null;

    /**
     * @type {HTMLElement|null}
     */
    emptyState = null;

    /**
     * @type {HTMLTemplateElement|null}
     */
    optionTemplate = null;

    /**
     * @type {HTMLElement|null}
     */
    chevronDownIcon = null;

    /**
     * @type {HTMLElement|null}
     */
    chevronUpIcon = null;

    /**
     * @type {HTMLElement|null}
     */
    ringOverlay = null;

    /**
     * @type {boolean}
     */
    isOpen = false;

    /**
     * @type {number}
     */
    openDropdownReservedPaddingBottom = 0;

    /**
     * @type {boolean}
     */
    allowFreeform = true;

    /**
     * @type {boolean}
     */
    isDisabled = false;

    /**
     * @type {'inline'|'summary'}
     */
    displayVariant = 'inline';

    /**
     * @type {string[]}
     */
    selectedValues = [];

    /**
     * @type {string[]}
     */
    initialSelectedValues = [];

    /**
     * Group tokens an option must belong to in order to be shown, or null when no group filter is active.
     * @type {Set<string>|null}
     */
    activeGroupFilter = null;

    /**
     * @param {HTMLElement} element
     */
    constructor(element) {
        this.element = element;
        this.bindElements();
        this.initializeSelection();
        this.addEventListeners();
    }

    /**
     * Finds and validates the DOM nodes used by the component.
     */
    bindElements() {
        const displayVariant = this.element.dataset.displayVariant;
        if (displayVariant !== 'inline' && displayVariant !== 'summary') {
            throw new Error(`Unsupported multi-select-group display variant: ${displayVariant ?? '(missing)'}`);
        }

        this.displayVariant = displayVariant;
        this.hiddenInput = this.element.querySelector('[data-multi-select-hidden-input]');
        this.dropdownTrigger = this.element.querySelector('[data-dropdown-trigger]');
        this.clearTrigger = this.element.querySelector('[data-clear-trigger]');
        this.controlShell = this.element.querySelector('[data-control-shell]');
        this.dropdownPanel = this.element.querySelector('[data-dropdown-panel]');
        this.displayValueWrapper = this.element.querySelector('[data-display-value-wrapper]');
        this.displayValue = this.element.querySelector('[data-display-value]');
        this.displayPlaceholder = this.element.querySelector('[data-display-placeholder]');
        this.displayPills = this.element.querySelector('[data-display-pills]');
        this.controlLabel = this.element.querySelector('[data-control-label]');
        this.searchInput = this.element.querySelector('[data-search-input]');
        this.clearSearchTrigger = this.element.querySelector('[data-clear-search]');
        this.selectAllTrigger = this.element.querySelector('[data-select-all-trigger]');
        this.optionList = this.element.querySelector('[data-options-list]');
        this.emptyState = this.element.querySelector('[data-empty-state]');
        this.optionTemplate = this.element.querySelector('[data-option-template]');
        this.chevronDownIcon = this.element.querySelector('[data-chevron-down]');
        this.chevronUpIcon = this.element.querySelector('[data-chevron-up]');
        this.ringOverlay = this.element.querySelector('[data-ring-overlay]');

        if (!(this.hiddenInput instanceof HTMLInputElement)) {
            throw new Error('Multi-select-group is missing the hidden input.');
        }

        if (!(this.dropdownTrigger instanceof HTMLElement)) {
            throw new Error('Multi-select-group is missing the dropdown trigger.');
        }

        if (!(this.clearTrigger instanceof HTMLButtonElement)) {
            throw new Error('Multi-select-group is missing the clear trigger.');
        }

        if (!(this.controlShell instanceof HTMLElement)) {
            throw new Error('Multi-select-group is missing the control shell.');
        }

        if (!(this.dropdownPanel instanceof HTMLElement)) {
            throw new Error('Multi-select-group is missing the dropdown panel.');
        }

        if (!(this.displayValueWrapper instanceof HTMLElement)) {
            throw new Error('Multi-select-group is missing the display value wrapper.');
        }

        if (this.displayVariant === 'summary' && !(this.displayValue instanceof HTMLElement)) {
            throw new Error('Multi-select-group summary variant is missing the display value.');
        }

        if (this.displayVariant === 'inline' && !(this.displayPlaceholder instanceof HTMLElement)) {
            throw new Error('Multi-select-group inline variant is missing the display placeholder.');
        }

        if (this.displayVariant === 'inline' && !(this.displayPills instanceof HTMLElement)) {
            throw new Error('Multi-select-group inline variant is missing the display pills container.');
        }

        if (!(this.searchInput instanceof HTMLInputElement)) {
            throw new Error('Multi-select-group is missing the search input.');
        }

        if (!(this.clearSearchTrigger instanceof HTMLButtonElement)) {
            throw new Error('Multi-select-group is missing the search clear trigger.');
        }

        if (!(this.optionList instanceof HTMLElement)) {
            throw new Error('Multi-select-group is missing the options list.');
        }

        if (!(this.emptyState instanceof HTMLElement)) {
            throw new Error('Multi-select-group is missing the empty state.');
        }

        if (!(this.optionTemplate instanceof HTMLTemplateElement)) {
            throw new Error('Multi-select-group is missing the option template.');
        }

        this.allowFreeform = this.element.dataset.allowFreeform === 'true';
        this.isDisabled = this.dropdownTrigger.dataset.disabled === 'true'
            || this.dropdownTrigger.getAttribute('aria-disabled') === 'true';
    }

    /**
     * Reads the initial comma-separated value and syncs the rendered state.
     */
    initializeSelection() {
        const initialValues = parseStoredValues(this.hiddenInput?.value ?? '');
        const selectedValues = [];

        initialValues.forEach((value) => {
            const canonicalValue = this.findCanonicalValue(value);

            if (canonicalValue !== null) {
                selectedValues.push(canonicalValue);
                return;
            }

            if (!this.allowFreeform) {
                return;
            }

            const option = this.createOption(value);
            if (option) {
                selectedValues.push(this.getOptionValue(option));
            }
        });

        this.selectedValues = uniqueValues(selectedValues);
        this.initialSelectedValues = [...this.selectedValues];
        this.syncState(false);
    }

    /**
     * Registers DOM event listeners.
     */
    addEventListeners() {
        this.dropdownTrigger?.addEventListener('click', () => {
            if (this.isDisabled) {
                return;
            }

            this.toggleDropdown();
        });

        this.dropdownTrigger?.addEventListener('keydown', (event) => {
            if (event.target !== this.dropdownTrigger || this.isDisabled) {
                return;
            }

            if (event.key === 'ArrowDown' && !this.isOpen) {
                event.preventDefault();
                this.openDropdown();
                return;
            }

            if ((event.key === 'Enter' || event.key === ' ') && !this.isOpen) {
                event.preventDefault();
                this.openDropdown();
            }
        });

        this.controlLabel?.addEventListener('click', (event) => {
            if (this.isDisabled) {
                return;
            }

            event.preventDefault();
            this.dropdownTrigger?.focus();
        });

        this.clearTrigger?.addEventListener('click', (event) => {
            event.preventDefault();
            event.stopPropagation();
            this.clear();
            if (this.isOpen) {
                this.searchInput?.focus();
            }
        });

        this.searchInput?.addEventListener('input', () => {
            this.updateSearchState();
        });

        this.searchInput?.addEventListener('keydown', (event) => {
            this.handleSearchKeyDown(event);
        });

        this.searchInput?.addEventListener('paste', (event) => {
            this.handleSearchPaste(event);
        });

        this.clearSearchTrigger?.addEventListener('click', (event) => {
            event.preventDefault();
            this.clearSearch(true);
        });

        this.selectAllTrigger?.addEventListener('click', (event) => {
            event.preventDefault();
            this.toggleVisibleSelections();
        });

        this.optionList?.addEventListener('click', (event) => {
            const option = /** @type {Element|null} */ (event.target instanceof Element
                ? event.target.closest('[data-option-button]')
                : null);

            if (!(option instanceof HTMLButtonElement)) {
                return;
            }

            event.preventDefault();
            this.toggleValue(this.getOptionValue(option));
        });

        this.optionList?.addEventListener('keydown', (event) => {
            const option = /** @type {HTMLElement|null} */ (event.target instanceof HTMLElement
                ? event.target.closest('[data-option-button]')
                : null);

            if (!(option instanceof HTMLButtonElement)) {
                return;
            }

            if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                this.toggleValue(this.getOptionValue(option));
                return;
            }

            if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
                event.preventDefault();
                this.focusAdjacentOption(option, event.key === 'ArrowDown' ? 1 : -1);
                return;
            }

            if (event.key === 'Escape') {
                event.preventDefault();
                this.closeDropdown();
                this.dropdownTrigger?.focus();
            }
        });

        this.displayPills?.addEventListener('click', (event) => {
            const removeTrigger = /** @type {HTMLElement|null} */ (event.target instanceof HTMLElement
                ? event.target.closest('[data-display-pill-remove]')
                : null);

            if (!(removeTrigger instanceof HTMLButtonElement)) {
                return;
            }

            event.preventDefault();
            event.stopPropagation();

            const pill = removeTrigger.closest('[data-display-pill]');
            if (!(pill instanceof HTMLElement)) {
                return;
            }

            const value = (pill.dataset.value ?? '').trim();
            if (value === '') {
                return;
            }

            this.removeValue(value);
            this.dropdownTrigger?.focus();
        });

        const form = this.element.closest('form');
        if (form instanceof HTMLFormElement) {
            form.addEventListener('reset', () => {
                requestAnimationFrame(() => {
                    this.setValues(this.initialSelectedValues, false);
                });
            });
        }
    }

    /**
     * Opens the dropdown and focuses the search field.
     */
    openDropdown() {
        if (this.isOpen || this.isDisabled || !this.dropdownPanel || !this.dropdownTrigger || !this.controlShell) {
            return;
        }

        this.element.dataset.state = 'open';
        this.controlShell.dataset.state = 'open';
        this.dropdownPanel.classList.remove('hidden');
        this.dropdownPanel.setAttribute('aria-hidden', 'false');
        this.dropdownTrigger.setAttribute('aria-expanded', 'true');
        this.isOpen = true;

        setElementHidden(this.chevronDownIcon, true);
        setElementHidden(this.chevronUpIcon, false);

        requestAnimationFrame(() => {
            this.updateRingOverlay();
            this.searchInput?.focus();
        });

        document.addEventListener('click', this.handleClickOutside);
        document.addEventListener('keydown', this.handleEscapeKey);
    }

    /**
     * Closes the dropdown and resets the search filter.
     */
    closeDropdown() {
        if (!this.isOpen || !this.dropdownPanel || !this.dropdownTrigger || !this.controlShell) {
            return;
        }

        delete this.controlShell.dataset.state;
        delete this.element.dataset.state;
        this.dropdownPanel.classList.add('hidden');
        this.dropdownPanel.setAttribute('aria-hidden', 'true');
        this.dropdownTrigger.setAttribute('aria-expanded', 'false');
        this.isOpen = false;

        setElementHidden(this.chevronDownIcon, false);
        setElementHidden(this.chevronUpIcon, true);

        if (this.ringOverlay instanceof HTMLElement) {
            this.ringOverlay.style.height = '';
        }

        this.openDropdownReservedPaddingBottom = 0;
        this.element.style.paddingBottom = '';

        this.clearSearch(false);

        document.removeEventListener('click', this.handleClickOutside);
        document.removeEventListener('keydown', this.handleEscapeKey);
    }

    /**
     * Toggles the dropdown state.
     */
    toggleDropdown() {
        if (this.isOpen) {
            this.closeDropdown();
            return;
        }

        this.openDropdown();
    }

    /**
     * @param {MouseEvent} event
     */
    handleClickOutside = (event) => {
        if (!(event.target instanceof Node)) {
            return;
        }

        if (!this.element.contains(event.target)) {
            this.closeDropdown();
        }
    };

    /**
     * @param {KeyboardEvent} event
     */
    handleEscapeKey = (event) => {
        if (event.key !== 'Escape' || !this.isOpen) {
            return;
        }

        this.closeDropdown();
        this.dropdownTrigger?.focus();
    };

    /**
     * @param {KeyboardEvent} event
     */
    handleSearchKeyDown(event) {
        if (!(this.searchInput instanceof HTMLInputElement)) {
            return;
        }

        if (event.key === 'Escape') {
            event.preventDefault();
            this.closeDropdown();
            this.dropdownTrigger?.focus();
            return;
        }

        if (event.key === 'ArrowDown') {
            const firstVisibleOption = this.getVisibleOptionButtons().find((option) => !option.disabled);
            if (firstVisibleOption) {
                event.preventDefault();
                firstVisibleOption.focus();
            }
            return;
        }

        if (event.key !== 'Enter' && event.key !== ',') {
            return;
        }

        const value = this.searchInput.value.trim();
        if (value === '') {
            return;
        }

        event.preventDefault();

        const canonicalValue = this.findCanonicalValue(value);
        if (canonicalValue !== null) {
            this.addValue(canonicalValue);
            this.clearSearch(true);
            return;
        }

        if (!this.allowFreeform) {
            return;
        }

        if (this.addValue(value)) {
            this.clearSearch(true);
        }
    }

    /**
     * @param {ClipboardEvent} event
     */
    handleSearchPaste(event) {
        if (!this.allowFreeform || !event.clipboardData) {
            return;
        }

        const values = splitFreeformInput(event.clipboardData.getData('Text'));
        if (values.length <= 1) {
            return;
        }

        event.preventDefault();
        values.forEach((value) => {
            this.addValue(value);
        });
        this.clearSearch(true);
    }

    /**
     * @returns {HTMLButtonElement[]}
     */
    getOptionButtons() {
        if (!(this.optionList instanceof HTMLElement)) {
            return [];
        }

        return [...this.optionList.querySelectorAll('[data-option-button]')].filter((option) => option instanceof HTMLButtonElement);
    }

    /**
     * @param {HTMLButtonElement} option
     * @returns {HTMLElement | null}
     */
    getOptionItem(option) {
        const item = option.closest('[data-option-item]');

        return item instanceof HTMLElement ? item : null;
    }

    /**
     * @param {HTMLButtonElement} option
     * @returns {boolean}
     */
    isOptionVisible(option) {
        const item = this.getOptionItem(option);

        return item instanceof HTMLElement && !item.hidden;
    }

    /**
     * @returns {HTMLButtonElement[]}
     */
    getVisibleOptionButtons() {
        return this.getOptionButtons().filter((option) => this.isOptionVisible(option));
    }

    /**
     * @param {HTMLButtonElement} option
     * @returns {string}
     */
    getOptionValue(option) {
        return (option.dataset.value ?? '').trim();
    }

    /**
     * @param {HTMLButtonElement} option
     * @returns {string}
     */
    getOptionLabel(option) {
        return (option.dataset.label ?? this.getOptionValue(option)).trim();
    }

    /**
     * @param {string} value
     * @returns {HTMLButtonElement | null}
     */
    findOptionButtonByValue(value) {
        const selector = `[data-option-button][data-value="${escapeSelectorValue(value)}"]`;
        const option = this.optionList?.querySelector(selector);

        return option instanceof HTMLButtonElement ? option : null;
    }

    /**
     * @param {string} value
     * @returns {string}
     */
    findOptionLabelByValue(value) {
        const option = this.findOptionButtonByValue(value);

        if (option instanceof HTMLButtonElement) {
            return this.getOptionLabel(option);
        }

        return value;
    }

    /**
     * @param {string} value
     * @returns {string | null}
     */
    findCanonicalValue(value) {
        const exactMatch = this.findOptionButtonByValue(value);
        if (exactMatch instanceof HTMLButtonElement) {
            return this.getOptionValue(exactMatch);
        }

        const normalizedCandidate = normalizeValue(value);
        if (normalizedCandidate === '') {
            return null;
        }

        for (const option of this.getOptionButtons()) {
            if (normalizeValue(this.getOptionValue(option)) === normalizedCandidate) {
                return this.getOptionValue(option);
            }

            if (normalizeValue(this.getOptionLabel(option)) === normalizedCandidate) {
                return this.getOptionValue(option);
            }
        }

        return null;
    }

    /**
     * @param {string} value
     * @returns {boolean}
     */
    addValue(value) {
        const normalizedValue = value.trim();
        if (normalizedValue === '') {
            return false;
        }

        let canonicalValue = this.findCanonicalValue(normalizedValue);

        if (canonicalValue === null) {
            if (!this.allowFreeform) {
                return false;
            }

            canonicalValue = normalizedValue;

            if (!this.dispatchTagAdding(canonicalValue)) {
                return false;
            }

            const option = this.createOption(canonicalValue);
            if (!(option instanceof HTMLButtonElement)) {
                return false;
            }

            canonicalValue = this.getOptionValue(option);
        } else if (this.selectedValues.includes(canonicalValue) || !this.dispatchTagAdding(canonicalValue)) {
            return false;
        }

        this.selectedValues = uniqueValues([...this.selectedValues, canonicalValue]);
        this.syncState();
        this.dispatchTagAdded(canonicalValue);

        return true;
    }

    /**
     * @param {string} value
     * @returns {boolean}
     */
    removeValue(value) {
        if (!this.selectedValues.includes(value)) {
            return false;
        }

        this.selectedValues = this.selectedValues.filter((selectedValue) => selectedValue !== value);
        this.syncState();
        this.dispatchTagRemoved(value);

        return true;
    }

    /**
     * @param {string} value
     */
    toggleValue(value) {
        if (this.selectedValues.includes(value)) {
            this.removeValue(value);
            return;
        }

        this.addValue(value);
    }

    /**
     * Selects or clears all currently visible options.
     */
    toggleVisibleSelections() {
        const visibleOptions = this.getVisibleOptionButtons().filter((option) => !option.disabled);
        if (visibleOptions.length === 0) {
            return;
        }

        const areAllVisibleOptionsSelected = visibleOptions.every((option) => this.selectedValues.includes(this.getOptionValue(option)));

        if (areAllVisibleOptionsSelected) {
            const visibleValues = visibleOptions.map((option) => this.getOptionValue(option));
            const removedValues = this.selectedValues.filter((value) => visibleValues.includes(value));

            if (removedValues.length === 0) {
                return;
            }

            this.selectedValues = this.selectedValues.filter((value) => !visibleValues.includes(value));
            this.syncState();
            removedValues.forEach((value) => {
                this.dispatchTagRemoved(value);
            });

            return;
        }

        const addedValues = [];

        visibleOptions.forEach((option) => {
            const value = this.getOptionValue(option);

            if (this.selectedValues.includes(value) || !this.dispatchTagAdding(value)) {
                return;
            }

            addedValues.push(value);
        });

        if (addedValues.length === 0) {
            return;
        }

        this.selectedValues = uniqueValues([...this.selectedValues, ...addedValues]);
        this.syncState();
        addedValues.forEach((value) => {
            this.dispatchTagAdded(value);
        });
    }

    /**
     * Clears every selected value.
     */
    clear() {
        [...this.selectedValues].forEach((value) => {
            this.removeValue(value);
        });
    }

    /**
     * @returns {string[]}
     */
    getValues() {
        return [...this.selectedValues];
    }

    /**
     * @param {string[]} values
     * @param {boolean} shouldTriggerChange
     */
    setValues(values, shouldTriggerChange = true) {
        const nextValues = [];

        values.forEach((value) => {
            const normalizedValue = value.trim();
            if (normalizedValue === '') {
                return;
            }

            const canonicalValue = this.findCanonicalValue(normalizedValue);
            if (canonicalValue !== null) {
                nextValues.push(canonicalValue);
                return;
            }

            if (!this.allowFreeform) {
                return;
            }

            const option = this.createOption(normalizedValue);
            if (option instanceof HTMLButtonElement) {
                nextValues.push(this.getOptionValue(option));
            }
        });

        this.selectedValues = uniqueValues(nextValues);
        this.syncState(shouldTriggerChange);
    }

    /**
     * @param {HTMLButtonElement} currentOption
     * @param {number} direction
     */
    focusAdjacentOption(currentOption, direction) {
        const visibleOptions = this.getVisibleOptionButtons().filter((option) => !option.disabled);
        const currentIndex = visibleOptions.indexOf(currentOption);

        if (visibleOptions.length === 0 || currentIndex === -1) {
            return;
        }

        const targetIndex = currentIndex + direction;
        const fallbackIndex = direction > 0 ? 0 : visibleOptions.length - 1;
        const nextOption = visibleOptions[targetIndex] ?? visibleOptions[fallbackIndex];

        nextOption?.focus();
    }

    /**
     * Updates every piece of rendered state derived from the selection.
     * @param {boolean} shouldTriggerChange
     */
    syncState(shouldTriggerChange = true) {
        this.getOptionButtons().forEach((option) => {
            const value = this.getOptionValue(option);
            const isSelected = this.selectedValues.includes(value);

            option.dataset.selected = isSelected ? 'true' : 'false';
            option.setAttribute('aria-selected', isSelected ? 'true' : 'false');
        });

        this.updateDisplayValue();
        this.updateHiddenInput(shouldTriggerChange);
        this.updateSearchState();
    }

    /**
     * Updates the collapsed button copy and clear-button visibility.
     */
    updateDisplayValue() {
        if (!(this.displayValueWrapper instanceof HTMLElement)) {
            return;
        }

        const selectionCount = this.selectedValues.length;
        const hasSelection = selectionCount > 0;

        this.element.dataset.hasSelection = hasSelection ? 'true' : 'false';
        setElementHidden(this.clearTrigger, !hasSelection);

        if (this.displayVariant === 'inline') {
            this.updateInlineDisplayValue(hasSelection);
            return;
        }

        if (!(this.displayValue instanceof HTMLElement)) {
            throw new Error('Multi-select-group summary variant is missing the display value.');
        }

        const placeholder = this.element.dataset.placeholder;
        const singularLabel = this.element.dataset.selectedSingularLabel;
        const pluralLabel = this.element.dataset.selectedPluralLabel;

        this.displayValue.textContent = hasSelection
            ? `${selectionCount} ${selectionCount === 1 ? singularLabel : pluralLabel}`
            : placeholder;

        this.displayValueWrapper.classList.remove('text-[var(--control-text)]', 'text-gray-400');
        this.displayValueWrapper.classList.add(
            hasSelection ? 'text-[var(--control-text)]' : 'text-gray-400'
        );
    }

    /**
     * @param {boolean} hasSelection
     */
    updateInlineDisplayValue(hasSelection) {
        if (!(this.displayPlaceholder instanceof HTMLElement) || !(this.displayPills instanceof HTMLElement)) {
            throw new Error('Multi-select-group inline variant is missing display elements.');
        }

        this.displayPills.replaceChildren(
            ...this.selectedValues.map((value) => this.createInlineDisplayPill(value))
        );
        setElementHidden(this.displayPlaceholder, hasSelection);
    }

    /**
     * @param {string} value
     * @returns {HTMLSpanElement}
     */
    createInlineDisplayPill(value) {
        const pill = document.createElement('span');
        pill.classList.add(...inlineDisplayPillClassNames);
        pill.dataset.displayPill = '';
        pill.dataset.value = value;
        const labelText = this.findOptionLabelByValue(value);

        const label = document.createElement('span');
        label.classList.add('tagPicker__label');
        label.textContent = labelText;

        pill.append(label);

        if (!this.isDisabled) {
            pill.append(this.createInlineDisplayPillRemoveButton(value, labelText));
        }

        return pill;
    }

    /**
     * @param {string} value
     * @param {string} label
     * @returns {HTMLButtonElement}
     */
    createInlineDisplayPillRemoveButton(value, label) {
        const button = document.createElement('button');

        button.type = 'button';
        button.dataset.displayPillRemove = '';
        button.classList.add(...inlineDisplayPillRemoveButtonClassNames);
        button.setAttribute('aria-label', t('Remove :label', {label}));
        button.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" class="svg-icon fg-body-xs"><use href="#icon-close"></use></svg>';

        return button;
    }

    /**
     * @param {boolean} shouldTriggerChange
     */
    updateHiddenInput(shouldTriggerChange) {
        if (!(this.hiddenInput instanceof HTMLInputElement)) {
            return;
        }

        const previousValue = this.hiddenInput.value;
        const nextValue = this.selectedValues.join(',');

        this.hiddenInput.value = nextValue;

        if (!shouldTriggerChange || previousValue === nextValue) {
            return;
        }

        this.hiddenInput.dispatchEvent(new Event('change', {bubbles: true}));
        this.element.dispatchEvent(new CustomEvent('multiSelectGroup:change', {
            bubbles: true,
            detail: this.getValues(),
        }));
    }

    /**
     * Updates the option filter and related UI.
     */
    updateSearchState() {
        const query = normalizeValue(this.searchInput?.value ?? '');
        let visibleOptionsCount = 0;

        this.getOptionButtons().forEach((option) => {
            const item = this.getOptionItem(option);
            if (!(item instanceof HTMLElement)) {
                return;
            }

            const label = this.getOptionLabel(option);
            const matchesQuery = query === '' || normalizeValue(label).includes(query);
            const matches = matchesQuery && this.matchesGroupFilter(option);

            setElementHidden(item, !matches);

            if (matches) {
                visibleOptionsCount += 1;
            }
        });

        setElementHidden(this.emptyState, visibleOptionsCount > 0);
        setElementHidden(this.clearSearchTrigger, (this.searchInput?.value ?? '') === '');
        this.updateSelectAllState();

        if (this.isOpen) {
            requestAnimationFrame(() => {
                this.updateRingOverlay();
            });
        }
    }

    /**
     * Narrows the visible options to those whose `data-group` is in the given set.
     * Options without a `data-group` are always shown. Pass an empty list to clear the filter.
     * @param {string[]} groups
     */
    setGroupFilter(groups) {
        this.activeGroupFilter = groups.length > 0 ? new Set(groups) : null;
        this.updateSearchState();
    }

    /**
     * @param {HTMLButtonElement} option
     * @returns {boolean}
     */
    matchesGroupFilter(option) {
        if (this.activeGroupFilter === null) {
            return true;
        }

        const group = option.dataset.group;

        return group === undefined || this.activeGroupFilter.has(group);
    }

    /**
     * @param {boolean} focusInput
     */
    clearSearch(focusInput) {
        if (!(this.searchInput instanceof HTMLInputElement)) {
            return;
        }

        this.searchInput.value = '';
        this.updateSearchState();

        if (focusInput) {
            this.searchInput.focus();
        }
    }

    /**
     * Updates the "select all" checkbox state.
     */
    updateSelectAllState() {
        if (!(this.selectAllTrigger instanceof HTMLButtonElement)) {
            return;
        }

        const visibleOptions = this.getVisibleOptionButtons().filter((option) => !option.disabled);
        const hasVisibleOptions = visibleOptions.length > 0;
        const areAllVisibleOptionsSelected = hasVisibleOptions
            && visibleOptions.every((option) => this.selectedValues.includes(this.getOptionValue(option)));

        this.selectAllTrigger.dataset.selected = areAllVisibleOptionsSelected ? 'true' : 'false';
        this.selectAllTrigger.setAttribute('aria-checked', areAllVisibleOptionsSelected ? 'true' : 'false');
        this.selectAllTrigger.disabled = this.isDisabled || !hasVisibleOptions;
    }

    /**
     * Updates the shared ring overlay to wrap the trigger and open panel.
     */
    updateRingOverlay() {
        if (!(this.ringOverlay instanceof HTMLElement) || !(this.dropdownPanel instanceof HTMLElement) || !(this.dropdownTrigger instanceof HTMLElement)) {
            return;
        }

        const triggerRect = this.dropdownTrigger.getBoundingClientRect();
        const panelRect = this.dropdownPanel.getBoundingClientRect();
        const elementRect = this.element.getBoundingClientRect();
        // The open-state layout reservation becomes part of the element box, so remove it before recalculating.
        const baseElementBottom = elementRect.bottom - this.openDropdownReservedPaddingBottom;
        const totalHeight = panelRect.bottom - triggerRect.top;
        const panelOverflowBelowComponent = panelRect.bottom - baseElementBottom;

        if (totalHeight > 0) {
            this.ringOverlay.style.height = `${totalHeight}px`;
        }

        this.openDropdownReservedPaddingBottom = panelOverflowBelowComponent > 0
            ? panelOverflowBelowComponent + openDropdownLayoutGap
            : 0;

        this.element.style.paddingBottom = this.openDropdownReservedPaddingBottom > 0
            ? `${this.openDropdownReservedPaddingBottom}px`
            : '';
    }

    /**
     * @param {string} value
     * @returns {HTMLButtonElement | null}
     */
    createOption(value) {
        if (!(this.optionTemplate instanceof HTMLTemplateElement) || !(this.optionList instanceof HTMLElement)) {
            return null;
        }

        const canonicalValue = value.trim();
        if (canonicalValue === '') {
            return null;
        }

        const existingOption = this.findOptionButtonByValue(canonicalValue);
        if (existingOption instanceof HTMLButtonElement) {
            return existingOption;
        }

        const optionFragment = this.optionTemplate.content.cloneNode(true);
        if (!(optionFragment instanceof DocumentFragment)) {
            return null;
        }

        const option = optionFragment.querySelector('[data-option-button]');
        const optionLabel = optionFragment.querySelector('[data-option-label]');

        if (!(option instanceof HTMLButtonElement) || !(optionLabel instanceof HTMLElement)) {
            throw new Error('Multi-select-group option template is invalid.');
        }

        option.dataset.value = canonicalValue;
        option.dataset.label = canonicalValue;
        option.dataset.selected = 'false';
        option.setAttribute('aria-selected', 'false');
        optionLabel.textContent = canonicalValue;

        this.optionList.insertBefore(optionFragment, this.emptyState ?? null);

        return option;
    }

    /**
     * @param {string} value
     * @returns {boolean}
     */
    dispatchTagAdding(value) {
        return this.element.dispatchEvent(new CustomEvent('tagsInput:tagAdding', {
            bubbles: true,
            cancelable: true,
            detail: value,
        }));
    }

    /**
     * @param {string} value
     */
    dispatchTagAdded(value) {
        this.element.dispatchEvent(new CustomEvent('tagsInput:tagAdded', {
            bubbles: true,
            detail: value,
        }));
    }

    /**
     * @param {string} value
     */
    dispatchTagRemoved(value) {
        this.element.dispatchEvent(new CustomEvent('tagsInput:tagRemoved', {
            bubbles: true,
            detail: value,
        }));
    }
}

/**
 *
 */
export function initMultiSelectGroup() {
    document.querySelectorAll('[data-multi-select-group]').forEach((element) => {
        const multiSelectGroup = new MultiSelectGroup(/** @type {HTMLElement} */ (element));
        /** @type {HTMLElement & { multiSelectGroupInstance?: MultiSelectGroup }} */ (element).multiSelectGroupInstance = multiSelectGroup;
    });
}

if (typeof window !== 'undefined') {
    document.readyState === 'loading'
        ? document.addEventListener('DOMContentLoaded', initMultiSelectGroup)
        : initMultiSelectGroup();
}
